import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import * as cookie from 'cookie';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useEffect, useRef, useState } from 'react';

import CategoryFaqSection from '@/components/CategoryComp/CategoryFaqSection';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import CategoriesSidebarWrapper from '@/components/ShopComp/CategoriesSidebarWrapper';
import BooksListWrapper from '@/components/ShopComp/ProductListWrapper';
import HeroSection from '@/components/V2/HomePage/HeroSection';

import { detectCountry, getActiveCountries } from '@/services/globalCountry';
import {
  getAllProductsSSR,
  getCategoryByNameSSR,
  getLandingPageSeoSSR,
} from '@/services/LandingPage';
import { formatEcomProductBooks } from '@/utils/ProductFormaterBooks';

interface Props {
  metaData: {
    title: string;
    description: string;
    keywords: string;
    image: string;
    openGraph: {
      title: string;
      description: string;
      image: string;
    };
  } | null;
  categories: {
    description: string;
    faq: { question: string; answer: string }[];
  } | null;
  ecomProducts: any[];
}

export interface IFaq {
  question: string;
  answer: string;
}

export const generateFaqSchema = (faqData: IFaq[]) => {
  if (!faqData || faqData.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

const CategoryPage: React.FC<Props> = ({
  metaData,
  categories,
  ecomProducts,
}) => {

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [editorLoaded, setEditorLoaded] = useState(false);

  const editorRef = useRef<any>();
  const { CKEditor, ClassicEditor } = editorRef.current || {};

  useEffect(() => {
    editorRef.current = {
      CKEditor: require('@ckeditor/ckeditor5-react'),
      ClassicEditor: require('@ckeditor/ckeditor5-build-classic'),
    };
    setEditorLoaded(true);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchKeyword = e.target.value;
    setSearchKeyword(newSearchKeyword);
  };


  const screens = useBreakpoint();



  const preprocessHTML = (html: string): string => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    const listItems = tempElement.querySelectorAll('li');

    listItems.forEach((li) => {
      for (let i = 0; i < li.childNodes.length; i++) {
        const node = li.childNodes[i];
        if (node instanceof HTMLElement && node.tagName === 'P') {
          while (node.firstChild) {
            li.appendChild(node.firstChild);
          }
          node.parentNode?.removeChild(node);
          i--;
        }
      }
    });

    return tempElement.innerHTML;
  };

  return (
    <>
      <Head>
        <title key='title'>
          {metaData?.title ||
            'Explore Finance Books at ZebraLearn: Your Online Bookstore'}
        </title>
        <meta
          name='description'
          content={
            metaData?.description ||
            'Discover best books publisher in India specializing in non-fiction books. ZebraLearn offers cutting-edge design & technology insights, simplifying complex topics.'
          }
          key='description'
        />
        <meta
          name='keywords'
          content={metaData?.keywords || 'books, learning, education'}
          key='keywords'
        />

        <meta
          property='og:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='og-title'
        />
        <meta
          property='og:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='og-description'
        />
        <meta
          property='og:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG-image.png'
          }
          key='og-image'
        />

        <meta
          property='og:image:type'
          content='image/webp'
          key='og-image-type-webp'
        />
        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type-png'
        />
        <meta
          name='twitter:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='twitter-title'
        />
        <meta
          name='twitter:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='twitter-description'
        />
        <meta
          name='twitter:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG+1.png?tr=f-png'
          }
          key='twitter-image'
        />
        <meta
          property='article:section'
          content='Website Section'
          key='article-section'
        />
        <meta
          property='article:tag'
          content={metaData?.keywords as string}
          key='article-tag'
        />

        {categories?.faq && categories.faq.length > 0 && (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(generateFaqSchema(categories.faq)),
            }}
          />
        )}
      </Head>
      <div className='flex flex-col'>
        <HeroSection />
        <div className='text-white pt-2 lg:pt-6 lg:px-12'>
          <div className='flex flex-col md:flex-row'>
            <ShouldRender check={screens.lg}>
              <CategoriesSidebarWrapper
                handleInputChange={handleInputChange}
                searchKeyword={searchKeyword}
                setSearchKeyword={setSearchKeyword}
                type='bundles'
              />
            </ShouldRender>
            <div className='flex-1  md:px-6'>
              <BooksListWrapper
                handleInputChange={handleInputChange}
                searchKeyword={searchKeyword}
                setSearchKeyword={setSearchKeyword}
                type='bundles'
                ecomProducts={ecomProducts}
              />
            </div>
          </div>
        </div>
        <div className='lg:mt-16'>
          <ShouldRender check={categories?.faq && categories?.faq.length > 0}>
            <div className='border-t border-t-grayZl-850 border-transparent  pt-8'>
              <CategoryFaqSection faqData={categories?.faq || []} />
            </div>
          </ShouldRender>

          <ShouldRender check={categories?.description && editorLoaded}>
            <div className='flex justify-center w-full px-4 md:px-6 lg:px-12 xl:px-[220px] bg-[#121212] py-6 md:py-10'>
              <div className='text-grayZl-500 w-full  '>
                <div className='customCkeditor customCkeditorSchool customCkeditorCategory text-grayZl-500 border-0'>
                  <CKEditor
                    disabled={true}
                    config={{
                      toolbar: [],
                      readOnly: true,
                      allowedContent: true,
                      extraAllowedContent: 'p(*)[class]',
                      contentsCss: '../../../../styles/ckEditor.scss',
                      styles: [
                        {
                          name: 'Normal',
                          element: 'p',
                        },
                        {
                          name: 'Large',
                          element: 'p',
                        },
                      ],
                      link: {
                        defaultTarget: '_blank',
                        decorators: {
                          openInNewTab: {
                            mode: 'manual',
                            label: 'Open in a new tab',
                            defaultValue: true,
                            attributes: {
                              target: '_blank',
                              rel: 'noopener noreferrer',
                            },
                          },
                        },
                      },
                    }}
                    className='customCkeditor border-0 customCkeditorSchool'
                    data={preprocessHTML(categories?.description as string)}
                    editor={ClassicEditor}
                  />
                </div>
              </div>
            </div>
          </ShouldRender>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  try {
    const { query, resolvedUrl, req } = ctx;

    const rawCookies = req.headers.cookie || '';
    const parsedCookies = cookie.parse(rawCookies);
    let country = parsedCookies['country']?.trim();

    if (!country) {
      const [activeCountries, detectedCode] = await Promise.all([
        getActiveCountries(),
        detectCountry(req),
      ]);

      if (activeCountries.length > 0) {
        const fallback = activeCountries.find((c: any) => c.code === 'IN');
        const found =
          activeCountries.find((c: any) => c.code === detectedCode) || fallback;

        country = found?.code || 'IN';
      }
    }

    const rawCategory = query.category;
    const category = Array.isArray(rawCategory)
      ? rawCategory[0]
      : rawCategory || '';

    const categoryName =
      resolvedUrl?.split('?')[0]?.split('/').filter(Boolean).at(-1) ?? null;

    const metaData = await getLandingPageSeoSSR((category as string) || '');

    const allHardCopyData = await getAllProductsSSR({
      page: 1,
      limit: 9999,
      category_id: 6,
      book_type: 'hardcover',
      country: country as string,
    });

    const formattedecomProductsData = formatEcomProductBooks(
      allHardCopyData || [],
    );

    const formattedMetaData = {
      title: metaData?.title || '',
      description: metaData?.description || '',
      author: metaData?.author || 'Unknown Author',
      image: metaData?.openGraph?.image ?? null,
      keywords: metaData?.keywords || '',
      openGraph: {
        title: metaData?.openGraph?.title ?? null,
        description: metaData?.openGraph?.description ?? null,
        image: metaData?.openGraph?.image ?? null,
      },
    };

    let categoryData = null;
    try {
      categoryData = await getCategoryByNameSSR(categoryName as string);
    } catch (err: any) {
      console.warn('⚠️ Category fetch failed, continuing SSR:', err.message);
    }

    const formattedCategory = {
      description: categoryData?.description || '',
      faq:
        categoryData?.faq?.map((item: IFaq) => ({
          question: item.question,
          answer: item.answer,
        })) || [],
    };

    return {
      props: {
        metaData: formattedMetaData,
        categories: formattedCategory,
        ecomProducts: formattedecomProductsData,
      },
    };
  } catch (error) {
    console.error('Top-level SSR error:', error);
    return {
      props: {
        metaData: null,
        categories: null,
        ecomProducts: [],
      },
    };
  }
};

export default CategoryPage;
